import React, { useEffect, useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { Box, Button, CircularProgress, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';

import { ProjectEstimate, ProjectLayoutModel } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { changesExist } from '@paragon/layout-models';
import { OptionalTooltip } from '@paragon/material';

import { downloadProjectFiles } from '../../store/actions/download';
import { useAppDispatch, useAppSelector } from '../hooks';
import { EstimateWizard } from './EstimateWizard';

interface Props {
  readonly projectGuid: string;
  readonly project: ProjectLayoutModel | undefined;
  /** The wizard takeover state is owned by the drawer, which also opens it from its header action. */
  readonly wizardOpen: boolean;
  readonly setWizardOpen: (open: boolean) => void;
}

export type EstimatesState = readonly ProjectEstimate[] | 'loading' | 'error';

export function useCreateEstimateDisabledReason(project: ProjectLayoutModel | undefined): string | null {
  const projectChangesExist = useAppSelector(changesExist);

  if (project == null) return 'Project is still loading';
  if (projectChangesExist) return 'Save your changes before creating an estimate';
  return null;
}

export function ProjectEstimatesTab({ projectGuid, project, wizardOpen, setWizardOpen }: Props) {
  const dispatch = useAppDispatch();
  const [estimates, setEstimates] = useState<EstimatesState>('loading');
  const createDisabledReason = useCreateEstimateDisabledReason(project);
  const [reloadCount, setReloadCount] = useState(0);

  // Load on mount (even if the wizard is already open, since it reuses this list) and reload after an estimate is created.
  useEffect(() => {
    let cancelled = false;
    setEstimates('loading');
    designService.estimates
      .getEstimatesForProject(projectGuid)
      .then((projectEstimates) => {
        if (!cancelled) setEstimates(projectEstimates);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setEstimates('error');
      });

    return () => {
      cancelled = true;
    };
  }, [projectGuid, reloadCount]);

  if (wizardOpen && project != null) {
    return (
      <EstimateWizard
        project={project}
        existingProjectEstimates={estimates}
        createDisabledReason={createDisabledReason}
        onClose={() => setWizardOpen(false)}
        onCreated={() => {
          setWizardOpen(false);
          setReloadCount((count) => count + 1);
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
          Estimates
        </Typography>
        <OptionalTooltip title={createDisabledReason} visible={createDisabledReason != null}>
          <Button
            variant="contained"
            startIcon={<RequestQuoteIcon />}
            disabled={createDisabledReason != null}
            onClick={() => setWizardOpen(true)}
          >
            Create Estimate
          </Button>
        </OptionalTooltip>
      </Box>

      {estimates === 'loading' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {estimates === 'error' && (
        <Typography variant="body2" color="error">
          Could not load estimates for this project.
        </Typography>
      )}

      {Array.isArray(estimates) &&
        (estimates.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No estimates yet. Create one to get started.
          </Typography>
        ) : (
          <List dense>
            {estimates.map((estimate) => (
              <ListItem
                key={estimate.guid}
                divider
                secondaryAction={
                  <OptionalTooltip
                    visible={estimate.projectFile != null}
                    title={`Download ${estimate.projectFile?.fileName}`}
                  >
                    <IconButton
                      edge="end"
                      disabled={estimate.projectFile == null}
                      onClick={() => {
                        if (estimate.projectFile != null)
                          dispatch(downloadProjectFiles(projectGuid, [estimate.projectFile.guid]));
                      }}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </OptionalTooltip>
                }
              >
                <ListItemText
                  primary={estimate.name}
                  secondary={`${new Date(estimate.created).toLocaleDateString()} · ${estimate.createdByEmail}`}
                />
              </ListItem>
            ))}
          </List>
        ))}
    </Box>
  );
}
